import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { InputControlLabel } from '@picsio/ui';

import localization from '../../../shared/strings';
import DetailsBlock from '../../DetailsBlock';
import Tooltip from '../../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from '../helpers/ReadOnlyFieldTooltip';
import { productFieldInputProps } from '../constants';
import buildComplianceFieldLabel from '../helpers/buildComplianceFieldLabel';
import {
  normalizeCountryCodeOfOrigin,
  validateCountryCodeOfOrigin,
  normalizeHarmonizedSystemCode,
  validateHarmonizedSystemCode,
} from '../helpers/validateComplianceFields';
import InventoryTextField from './InventoryTextField';
import Levels from './Levels';
import AggregatedBooleanField from '../aggregated/fields/AggregatedBooleanField';
import AggregatedBoundScalarField from '../aggregated/fields/AggregatedBoundScalarField';
import { aggregatedInventoryFieldsPropType } from '../aggregated/helpers/aggregatedFieldPropTypes';
import { isAggregatedFieldMultiple } from '../aggregated/helpers/aggregatedFieldHelpers';
import { resolveAggregatedFieldTooltip } from '../aggregated/helpers/resolveAggregatedFieldTooltip';
import s from '../styles.module.scss';

const l10n = localization.PRODUCT_INFO.inventory;

const validateCountryField = (value) => validateCountryCodeOfOrigin(value, {
  invalidMessage: l10n.countryCodeOfOriginInvalid,
});
const validateHarmonizedSystemField = (value) => validateHarmonizedSystemCode(value, {
  invalidMessage: l10n.harmonizedSystemCodeInvalid,
});

const Inventory = ({
  tracked,
  sku,
  barcode,
  continueSelling,
  inventoryLevels,
  diff,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
  taxCode,
  countryCodeOfOrigin,
  harmonizedSystemCode,
  availableForSale,
  hideSku,
  hideBarcode,
  hideCompliance,
  hideTaxCode,
  hideInventoryPolicy,
  hideTracked,
  skuLabel,
  readOnly,
  readOnlyTooltip,
  skuReadOnly,
  barcodeReadOnly,
  onChangeSku,
  onChangeBarcode,
  onChangeTracked,
  onChangeInventoryPolicy,
  onChangeTaxCode,
  onChangeCountryCodeOfOrigin,
  onChangeHarmonizedSystemCode,
  onChangeAvailableForSale,
  aggregatedFields,
}) => {
  const saveTracked = useCallback(() => {
    if (readOnly) return;
    onChangeTracked(!tracked);
  }, [tracked, onChangeTracked, readOnly]);

  const saveInventoryPolicy = useCallback(() => {
    if (readOnly) return;
    onChangeInventoryPolicy(continueSelling ? 'DENY' : 'CONTINUE');
  }, [continueSelling, onChangeInventoryPolicy, readOnly]);

  const saveAvailableForSale = useCallback(() => {
    if (readOnly || !onChangeAvailableForSale) return;
    onChangeAvailableForSale(!availableForSale);
  }, [availableForSale, onChangeAvailableForSale, readOnly]);

  const skuFieldReadOnly = readOnly || skuReadOnly;
  const barcodeFieldReadOnly = readOnly || barcodeReadOnly;
  const skuMultiple = isAggregatedFieldMultiple(aggregatedFields?.sku);
  const barcodeMultiple = isAggregatedFieldMultiple(aggregatedFields?.barcode);
  const trackedMultiple = isAggregatedFieldMultiple(aggregatedFields?.tracked);
  const countryMultiple = isAggregatedFieldMultiple(aggregatedFields?.countryCodeOfOrigin);
  const harmonizedMultiple = isAggregatedFieldMultiple(aggregatedFields?.harmonizedSystemCode);

  const trackedLabel = (
    <Tooltip content={diff?.tracked ? 'Unpublished changes' : null} placement="top">
      <span className={diff?.tracked ? s.diffDot : undefined}>Track quantity</span>
    </Tooltip>
  );

  return (
    <DetailsBlock
      dataQa="productInventory"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
    >
      <If condition={!hideSku || !hideBarcode}>
        <div className={s.row}>
          <If condition={!hideSku}>
            <Choose>
              <When condition={skuMultiple}>
                <AggregatedBoundScalarField
                  fieldKey="sku"
                  field={aggregatedFields.sku}
                  label={<Tooltip content={diff?.sku ? 'Unpublished changes' : null} placement="top"><span className={diff?.sku ? s.diffDot : undefined}>{skuLabel}</span></Tooltip>}
                  readOnly={readOnly}
                  readOnlyTooltip={readOnlyTooltip}
                />
              </When>
              <Otherwise>
                <InventoryTextField
                  value={sku || ''}
                  label={<Tooltip content={diff?.sku ? 'Unpublished changes' : null} placement="top"><span className={diff?.sku ? s.diffDot : undefined}>{skuLabel}</span></Tooltip>}
                  readOnly={skuFieldReadOnly}
                  readOnlyTooltip={readOnlyTooltip}
                  tooltip={resolveAggregatedFieldTooltip({
                    field: aggregatedFields?.sku,
                    layerReadOnly: readOnly,
                    layerReadOnlyTooltip: readOnlyTooltip,
                    isIdentity: true,
                    canSet: !skuFieldReadOnly,
                    canClear: false,
                  })}
                  inputProps={productFieldInputProps}
                  onCommit={onChangeSku}
                />
              </Otherwise>
            </Choose>
          </If>
          <If condition={!hideBarcode}>
            <Choose>
              <When condition={barcodeMultiple}>
                <AggregatedBoundScalarField
                  fieldKey="barcode"
                  field={aggregatedFields.barcode}
                  label={<Tooltip content={diff?.barcode ? 'Unpublished changes' : null} placement="top"><span className={diff?.barcode ? s.diffDot : undefined}>Barcode (ISBN, UPC, GTIN, etc.)</span></Tooltip>}
                  readOnly={readOnly}
                  readOnlyTooltip={readOnlyTooltip}
                />
              </When>
              <Otherwise>
                <InventoryTextField
                  value={barcode || ''}
                  label={<Tooltip content={diff?.barcode ? 'Unpublished changes' : null} placement="top"><span className={diff?.barcode ? s.diffDot : undefined}>Barcode (ISBN, UPC, GTIN, etc.)</span></Tooltip>}
                  readOnly={barcodeFieldReadOnly}
                  readOnlyTooltip={readOnlyTooltip}
                  tooltip={resolveAggregatedFieldTooltip({
                    field: aggregatedFields?.barcode,
                    layerReadOnly: readOnly,
                    layerReadOnlyTooltip: readOnlyTooltip,
                    isIdentity: true,
                    canSet: !barcodeFieldReadOnly,
                    canClear: false,
                  })}
                  inputProps={productFieldInputProps}
                  onCommit={onChangeBarcode}
                />
              </Otherwise>
            </Choose>
          </If>
        </div>
        <div className={s.hr} />
      </If>
      <If condition={!hideTracked}>
        <Choose>
          <When condition={trackedMultiple}>
            <AggregatedBooleanField
              fieldKey="tracked"
              field={aggregatedFields.tracked}
              label={trackedLabel}
              dialogLabel="Track quantity"
              readOnly={readOnly}
              readOnlyTooltip={readOnlyTooltip}
            />
          </When>
          <Otherwise>
            <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
              <div className={s.row}>
                <InputControlLabel
                  control="checkbox"
                  label={trackedLabel}
                  value={tracked}
                  disabled={readOnly}
                  onChange={saveTracked}
                />
              </div>
            </ReadOnlyFieldTooltip>
          </Otherwise>
        </Choose>
        <If condition={tracked && !hideInventoryPolicy}>
          <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
            <div className={s.row}>
              <InputControlLabel
                control="checkbox"
                label={<Tooltip content={diff?.inventoryPolicy ? 'Unpublished changes' : null} placement="top"><span className={diff?.inventoryPolicy ? s.diffDot : undefined}>Continue selling when out of stock</span></Tooltip>}
                value={continueSelling}
                disabled={readOnly}
                onChange={saveInventoryPolicy}
              />
            </div>
          </ReadOnlyFieldTooltip>
        </If>
        <If condition={tracked && inventoryLevels}>
          <Levels data={inventoryLevels} />
        </If>
      </If>
      <If condition={onChangeAvailableForSale}>
        <div className={s.hr} />
        <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
          <div className={s.row}>
            <InputControlLabel
              control="checkbox"
              label={(
                <Tooltip content={diff?.availableForSale ? 'Unpublished changes' : null} placement="top">
                  <span className={diff?.availableForSale ? s.diffDot : undefined}>{l10n.availableForSale}</span>
                </Tooltip>
              )}
              value={availableForSale}
              disabled={readOnly}
              onChange={saveAvailableForSale}
            />
          </div>
        </ReadOnlyFieldTooltip>
      </If>
      <If condition={!hideTaxCode}>
        <div className={s.hr} />
        <div className={s.row}>
          <InventoryTextField
            value={taxCode || ''}
            label={(
              <Tooltip content={diff?.taxCode ? 'Unpublished changes' : null} placement="top">
                <span className={diff?.taxCode ? s.diffDot : undefined}>{l10n.taxCode}</span>
              </Tooltip>
            )}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
            onCommit={onChangeTaxCode}
          />
        </div>
      </If>
      <If condition={!hideCompliance}>
        <div className={s.hr} />
        <div className={s.row}>
          <Choose>
            <When condition={countryMultiple}>
              <AggregatedBoundScalarField
                fieldKey="countryCodeOfOrigin"
                field={aggregatedFields.countryCodeOfOrigin}
                label={buildComplianceFieldLabel({
                  label: l10n.countryCodeOfOrigin,
                  diff: diff?.countryCodeOfOrigin,
                  infoTooltip: l10n.countryCodeOfOriginTooltip,
                })}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
              />
            </When>
            <Otherwise>
              <InventoryTextField
                value={countryCodeOfOrigin || ''}
                label={buildComplianceFieldLabel({
                  label: l10n.countryCodeOfOrigin,
                  diff: diff?.countryCodeOfOrigin,
                  infoTooltip: l10n.countryCodeOfOriginTooltip,
                })}
                placeholder={l10n.countryCodeOfOriginPlaceholder}
                normalize={normalizeCountryCodeOfOrigin}
                validate={validateCountryField}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
                tooltip={resolveAggregatedFieldTooltip({
                  field: aggregatedFields?.countryCodeOfOrigin,
                  layerReadOnly: readOnly,
                  layerReadOnlyTooltip: readOnlyTooltip,
                  canSet: !readOnly,
                  canClear: false,
                })}
                onCommit={onChangeCountryCodeOfOrigin}
              />
            </Otherwise>
          </Choose>
          <Choose>
            <When condition={harmonizedMultiple}>
              <AggregatedBoundScalarField
                fieldKey="harmonizedSystemCode"
                field={aggregatedFields.harmonizedSystemCode}
                label={buildComplianceFieldLabel({
                  label: l10n.harmonizedSystemCode,
                  diff: diff?.harmonizedSystemCode,
                  infoTooltip: l10n.harmonizedSystemCodeTooltip,
                })}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
              />
            </When>
            <Otherwise>
              <InventoryTextField
                value={harmonizedSystemCode || ''}
                label={buildComplianceFieldLabel({
                  label: l10n.harmonizedSystemCode,
                  diff: diff?.harmonizedSystemCode,
                  infoTooltip: l10n.harmonizedSystemCodeTooltip,
                })}
                placeholder={l10n.harmonizedSystemCodePlaceholder}
                normalize={normalizeHarmonizedSystemCode}
                validate={validateHarmonizedSystemField}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
                tooltip={resolveAggregatedFieldTooltip({
                  field: aggregatedFields?.harmonizedSystemCode,
                  layerReadOnly: readOnly,
                  layerReadOnlyTooltip: readOnlyTooltip,
                  canSet: !readOnly,
                  canClear: false,
                })}
                onCommit={onChangeHarmonizedSystemCode}
              />
            </Otherwise>
          </Choose>
        </div>
      </If>
    </DetailsBlock>
  );
};

Inventory.propTypes = {
  tracked: PropTypes.bool,
  sku: PropTypes.string,
  barcode: PropTypes.string,
  continueSelling: PropTypes.bool,
  availableForSale: PropTypes.bool,
  inventoryLevels: PropTypes.arrayOf(PropTypes.shape({})),
  taxCode: PropTypes.string,
  countryCodeOfOrigin: PropTypes.string,
  harmonizedSystemCode: PropTypes.string,
  diff: PropTypes.shape({
    sku: PropTypes.bool,
    barcode: PropTypes.bool,
    tracked: PropTypes.bool,
    inventoryPolicy: PropTypes.bool,
    availableForSale: PropTypes.bool,
    taxCode: PropTypes.bool,
    countryCodeOfOrigin: PropTypes.bool,
    harmonizedSystemCode: PropTypes.bool,
  }),
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node.isRequired,
  hideSku: PropTypes.bool,
  hideBarcode: PropTypes.bool,
  hideCompliance: PropTypes.bool,
  hideTaxCode: PropTypes.bool,
  hideInventoryPolicy: PropTypes.bool,
  hideTracked: PropTypes.bool,
  skuLabel: PropTypes.node,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  skuReadOnly: PropTypes.bool,
  barcodeReadOnly: PropTypes.bool,
  aggregatedFields: aggregatedInventoryFieldsPropType,
  onChangeTaxCode: PropTypes.func,
  onChangeCountryCodeOfOrigin: PropTypes.func,
  onChangeHarmonizedSystemCode: PropTypes.func,
  onChangeSku: PropTypes.func.isRequired,
  onChangeBarcode: PropTypes.func.isRequired,
  onChangeTracked: PropTypes.func.isRequired,
  onChangeInventoryPolicy: PropTypes.func.isRequired,
  onChangeAvailableForSale: PropTypes.func,
};

Inventory.defaultProps = {
  tracked: false,
  sku: '',
  barcode: '',
  continueSelling: false,
  availableForSale: false,
  inventoryLevels: null,
  taxCode: '',
  countryCodeOfOrigin: '',
  harmonizedSystemCode: '',
  diff: {},
  hideSku: false,
  hideBarcode: false,
  hideCompliance: false,
  hideTaxCode: false,
  hideInventoryPolicy: false,
  hideTracked: false,
  skuLabel: 'SKU (Stock Keeping Unit)',
  readOnly: false,
  readOnlyTooltip: null,
  skuReadOnly: false,
  barcodeReadOnly: false,
  onChangeTaxCode: () => null,
  onChangeCountryCodeOfOrigin: () => null,
  onChangeHarmonizedSystemCode: () => null,
  onChangeAvailableForSale: null,
  aggregatedFields: null,
};

export default memo(Inventory);
